import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { GetMyPassengerResponse } from '../../types/auth.js'
import type { UpdatePassengerRequest } from '../../types/client.js'

import { apiClient } from '../../lib/client.js'
import { meKeys } from './keys.js'

interface UpdateMyPassengerParams {
  passengerId: string
  data: UpdatePassengerRequest
}

export const useUpdateMyPassengerMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ passengerId, data }: UpdateMyPassengerParams) => {
      const response = await apiClient.client.put<GetMyPassengerResponse>(
        `/me/passengers/${passengerId}`,
        data
      )
      return response.data
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: meKeys.passengers })
    },
    onMutate: async ({ passengerId, data }) => {
      await queryClient.cancelQueries({ queryKey: meKeys.passengers })

      const previousPassengers = queryClient.getQueryData<GetMyPassengerResponse[]>(
        meKeys.passengers
      )

      if (previousPassengers) {
        queryClient.setQueryData<GetMyPassengerResponse[]>(
          meKeys.passengers,
          previousPassengers.map((p) =>
            p.id === passengerId
              ? {
                  ...p,
                  firstName: data.firstName ?? p.firstName,
                  lastName: data.lastName ?? p.lastName,
                  birthDate: data.birthDate ?? p.birthDate,
                  gender: (data.gender as 'FEMALE' | 'MALE') ?? p.gender,
                  nationalityCode: data.nationalityCode ?? p.nationalityCode,
                  updatedAt: new Date().toISOString(),
                }
              : p
          )
        )
      }

      return { previousPassengers }
    },
  })
}
